import { DeliveryStatusEnum, ShippingTypeEnum } from "@/enum/shipping-type";
import { Tags } from "@/enum/tags";
import {
  blobDownloadResponseHandler,
  BlobDownload,
  EmptyResponse,
} from "@/utils/blob-response-handler";
import { toQueryParams } from "@/utils/to-query-params";
import { apiSlice } from "./http";

const REBUILT_FROM_DATA_HEADER: string = "x-rebuilt-from-data";

const MATCHES_SENT_VALUE: string = "true";

export interface RebuiltDownload extends BlobDownload {
  readonly matchesSent: boolean;
}

const rebuiltDownloadResponseHandler = async (
  response: Response,
): Promise<RebuiltDownload | EmptyResponse> => {
  const matchesSent: boolean =
    response.headers.get(REBUILT_FROM_DATA_HEADER) === MATCHES_SENT_VALUE;

  const parsed: BlobDownload | EmptyResponse =
    await blobDownloadResponseHandler<EmptyResponse>(response);

  if (!("blob" in parsed)) return parsed as EmptyResponse;

  return { blob: parsed.blob, filename: parsed.filename, matchesSent };
};

export interface DashboardRangeParams {
  start: string;
  end: string;
}

export interface ShippingHistoryParams extends DashboardRangeParams {
  page: number;
  perPage: number;
}

export interface ShippingHistoryMeta {
  total: number;
  currentPage: number;
  lastPage: number;
  perPage: number;
}

export interface ShippingHistoryPage {
  data: ShippingHistoryResponse[];
  meta: ShippingHistoryMeta;
}

export interface DashboardCycleSummaryResponse {
  interval: string;
  days_to_close: number;
  processed: number;
  approved_percentage: number;
  previous_processed: number;
  previous_cycle_interval: string;
}

export interface DashboardCardResponse {
  total: number;
  resolved: number;
  pending: number;
  resolved_percentage: number;
  delta_percentage: number;
}

export interface DashboardReversalsResponse {
  total: number;
  payroll_discount_amount: string;
  loss_amount: string;
  previous_cycles_average: string;
  previous_cycles_considered: number;
}

export interface DashboardSummaryResponse {
  losses: DashboardCardResponse;
  payroll_discount: DashboardCardResponse;
  reversals: DashboardReversalsResponse;
  hr_approval: DashboardCardResponse;
}

export interface DashboardPendingActionsResponse {
  pending_losses: number;
  waiting_hr: number;
  rejected_by_hr: number;
  cron_failure: boolean;
}

export interface ShippingDeliveryResponse {
  id: number;
  name: string;
  email: string;
  status: DeliveryStatusEnum;
  read_confirmed_at: string | null;
  failed_at: string | null;
}

export interface ShippingHistoryResponse {
  id: number;
  sent_at: string | null;
  kind: ShippingTypeEnum;
  cycle: string;
  recipients: ShippingDeliveryResponse[];
}

const RESOURCE_URL: string = "/conciliation-dashboard";

export const conciliationDashboardApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getDashboardCycleSummary: builder.query<
      DashboardCycleSummaryResponse,
      DashboardRangeParams
    >({
      query: (range) => ({
        url: `${RESOURCE_URL}/cycle-summary?${toQueryParams(range)}`,
        method: "GET",
      }),
      providesTags: [Tags.DASHBOARD_CYCLE],
    }),

    getDashboardSummary: builder.query<
      DashboardSummaryResponse,
      DashboardRangeParams
    >({
      query: (range) => ({
        url: `${RESOURCE_URL}/summary?${toQueryParams(range)}`,
        method: "GET",
      }),
      providesTags: [Tags.DASHBOARD_SUMMARY],
    }),

    getDashboardPendingActions: builder.query<
      DashboardPendingActionsResponse,
      void
    >({
      query: () => ({ url: `${RESOURCE_URL}/pending-actions`, method: "GET" }),
      providesTags: [Tags.DASHBOARD_PENDING],
    }),

    downloadShipping: builder.query<RebuiltDownload | EmptyResponse, number>({
      query: (id) => ({
        url: `/shipping/${id}/download`,
        method: "GET",
        responseHandler: (response: Response) =>
          rebuiltDownloadResponseHandler(response),
      }),
    }),

    resendShippingDelivery: builder.mutation<
      void,
      { id: number; reason?: string }
    >({
      query: ({ id, reason }) => ({
        url: `/shipping-delivery/${id}/resend`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: [Tags.SHIPPING_HISTORY],
    }),

    getDashboardShippingHistory: builder.query<
      ShippingHistoryPage,
      ShippingHistoryParams
    >({
      query: (params) => ({
        url: `${RESOURCE_URL}/shipping-history?${toQueryParams(params)}`,
        method: "GET",
      }),
      providesTags: [Tags.SHIPPING_HISTORY],
    }),
  }),
});

export const {
  useGetDashboardCycleSummaryQuery,
  useGetDashboardSummaryQuery,
  useGetDashboardPendingActionsQuery,
  useGetDashboardShippingHistoryQuery,
  useResendShippingDeliveryMutation,
  useLazyDownloadShippingQuery,
} = conciliationDashboardApi;
